import { useEffect } from "react";
import { useSelector } from "react-redux";

import { useMedplum } from "@medplum/react";

import { DevelopmentFeatureFlags, useFeature } from "@/feature-flags";
import { useAppDispatch } from "@/store";
import {
  buildDotPhraseCacheKey,
  loadInsertableDotPhrasesThunk,
  selectInsertableDotPhrases,
  selectInsertableDotPhrasesCacheKey,
} from "@/store/slices/dot-phrases";

import { InsertableDotPhrase } from "../types/insertable-dot-phrase";

import useDotPhraseAuthor from "./useDotPhraseAuthor";

const NO_DOT_PHRASES: InsertableDotPhrase[] = [];

export default function useInsertableDotPhrases(): InsertableDotPhrase[] {
  const medplum = useMedplum();
  const dispatch = useAppDispatch();
  const isEnabled = useFeature(DevelopmentFeatureFlags.DotPhrases);
  const { practiceId, practitionerId } = useDotPhraseAuthor();
  const dotPhrases = useSelector(selectInsertableDotPhrases);
  const loadedCacheKey = useSelector(selectInsertableDotPhrasesCacheKey);
  const cacheKey = buildDotPhraseCacheKey(practiceId, practitionerId);

  useEffect(() => {
    if (!isEnabled) return;
    if (!cacheKey || !practiceId || !practitionerId) return;
    if (loadedCacheKey === cacheKey) return;

    dispatch(loadInsertableDotPhrasesThunk({ medplum, practiceId, practitionerId, cacheKey }));
  }, [isEnabled, cacheKey, loadedCacheKey, practiceId, practitionerId, medplum, dispatch]);

  const isCacheCurrent = cacheKey !== undefined && cacheKey === loadedCacheKey;

  return isEnabled && isCacheCurrent ? dotPhrases : NO_DOT_PHRASES;
}
